import { useNavigate, useParams } from "@tanstack/react-router";
import {
  Archive,
  ChevronsRight,
  CircleCheck,
  FilePen,
  PanelRightOpen,
  Trash2,
  Undo2,
} from "lucide-react";
import { Fragment, useState } from "react";
import { type Invoice, send, useChange, useInvoice } from "../api";
import { type SaveStatus, saveStatusText } from "../autosave";
import {
  DocumentLink,
  DocumentPane,
  invoiceDocument,
  useDocumentPane,
  useMedia,
} from "../components/DocumentViewer";
import { Dialog, DialogForm, Empty, ErrorNote, usePageTitle } from "../components/ui";
import { useKeptOnLeaving, useNotify } from "../notify";
import { DocumentStep } from "./InvoiceDocumentStep";
import { LotsStep } from "./InvoiceLotsStep";
import { ReceiveStep } from "./InvoiceReceiveStep";
import { useReviewAutosave } from "./reviewAutosave";

/**
 * One wording on every width. The long forms only ever appeared on desktop and the short ones on a
 * phone, which made the same three steps read as two different things depending on what you picked
 * the invoice up on (Tyler, 2026-09-23). The longer phrasing stays as the accessible name, where
 * having more words costs nothing.
 */
const STEPS = [
  { label: "Invoice", spoken: "The invoice" },
  { label: "Specs", spoken: "Lots and specs" },
  { label: "Receive", spoken: "Receive" },
] as const;
/**
 * The review keyed by its invoice, so going straight from one invoice to another opens the second
 * afresh: its autosave and every pass start from that invoice, not from the one just left.
 */
export function InvoiceReviewRoute() {
  const { invoiceId } = useParams({ from: "/invoices/$invoiceId" });
  return <InvoiceReviewPage key={invoiceId} />;
}

/**
 * Reviewing an invoice before any of it becomes stock, in the three passes Tyler drew (2026-09-20):
 * read the document as a document, then confirm what each lot actually is, then receive it.
 *
 * The first pass shows the invoice the way the supplier printed it, because that is the thing the
 * brewer is holding. Nothing here corrects the document: where our own arithmetic disagrees with a
 * printed total, both are shown and the brewer decides (invariant 6).
 */
export function InvoiceReviewPage() {
  const { invoiceId } = useParams({ from: "/invoices/$invoiceId" });
  const { data: invoice, isLoading, error } = useInvoice(invoiceId);
  usePageTitle(
    [invoice?.supplierName, invoice?.invoiceNumber ? `#${invoice.invoiceNumber}` : null]
      .filter(Boolean)
      .join(" ") || "Invoice",
    "Invoices",
  );
  // The screen is saved as it is used, and a draft reopens where it was left (Tyler, 2026-09-24).
  const autosave = useReviewAutosave(invoice);
  const [chosenStep, setChosenStep] = useState<number | null>(null);
  const step = chosenStep ?? autosave.initial?.step ?? 0;
  const setStep = (next: number) => {
    setChosenStep(next);
    autosave.update({ step: next });
  };
  const departureHandled = useDraftKeptOnLeaving(invoice);
  // Room for the document beside the passes while reviewing (Tyler, 2026-09-24).
  const wide = useMedia("(min-width: 1400px)");
  const [paneShown, setPaneShown] = useDocumentPane();

  if (isLoading) return <div className="page-body" />;
  if (error || !invoice) {
    return (
      <div className="page-body">
        <Empty title="No such invoice">
          <ErrorNote error={error} />
        </Empty>
      </div>
    );
  }
  const committed = invoice.status !== "draft";
  const doc = invoice.file ? invoiceDocument(invoice.id, invoice.file) : null;
  // Beside the passes while the invoice is being reviewed. Once received, the document is a layer
  // opened over the page, which is what the name in the header does everywhere.
  const beside = !committed && wide && doc !== null && paneShown;

  const content = (
    <>
      <header className="page-head">
        <div className="page-head-title">
          <StatusIcon invoice={invoice} />
          <h1>
            {invoice.supplierName ?? "Invoice"}
            {invoice.invoiceNumber ? ` ${invoice.invoiceNumber}` : ""}
          </h1>
        </div>
        <div className="page-head-actions">
          {!committed && wide && doc && !paneShown ? (
            <button type="button" className="button" onClick={() => setPaneShown(true)}>
              <PanelRightOpen size={14} /> Show document
            </button>
          ) : null}
          <InvoiceActions
            invoice={invoice}
            onHandledDeparture={() => {
              departureHandled.current = true;
            }}
          />
        </div>
        {doc ? <DocumentLink doc={doc} preview={!committed && !beside} /> : null}
        {!committed ? <SaveStatusNote status={autosave.status} /> : null}
      </header>

      {committed ? null : (
        <nav className="wizard-steps" aria-label="Review steps">
          {STEPS.map(({ label, spoken }, index) => (
            <Fragment key={label}>
              {index > 0 ? <ChevronsRight className="wizard-arrow" size={16} aria-hidden /> : null}
              <button
                type="button"
                className={`wizard-step ${index === step ? "current" : ""}`}
                aria-current={index === step ? "step" : undefined}
                aria-label={spoken}
                onClick={() => setStep(index)}
              >
                <span className="wizard-number">{index + 1}</span>
                <span className="step-label">{label}</span>
              </button>
            </Fragment>
          ))}
        </nav>
      )}

      <div className="page-body stack">
        {committed || step === 0 ? <DocumentStep invoice={invoice} readOnly={committed} /> : null}
        {!committed && step === 1 ? (
          <LotsStep
            invoice={invoice}
            onDone={() => setStep(2)}
            saved={autosave.initial?.lots}
            onChange={(lots) => autosave.update({ lots })}
          />
        ) : null}
        {!committed && step === 2 ? (
          <ReceiveStep
            invoice={invoice}
            savedKinds={autosave.initial?.kindLocations}
            onKinds={(kindLocations) => autosave.update({ kindLocations })}
            savedSplit={autosave.initial?.splitLocations}
            onSplit={(splitLocations) => autosave.update({ splitLocations })}
          />
        ) : null}
        {!committed && step === 0 ? (
          <div className="row">
            <span className="spacer" />
            <button type="button" className="button primary" onClick={() => setStep(1)}>
              Next
            </button>
          </div>
        ) : null}
      </div>
    </>
  );

  if (!beside || !doc) return content;
  return (
    <div className="review-split">
      <div className="review-main">{content}</div>
      <DocumentPane doc={doc} onHide={() => setPaneShown(false)} />
    </div>
  );
}
/**
 * Walking away from a half-reviewed invoice says where it went (Tyler, 2026-09-21). The draft has
 * been on the server since it was created, so nothing is written here. It says the draft is still
 * waiting, which is the bit that is not obvious once the screen has gone.
 *
 * The review itself saves as it is typed (reviewAutosave.ts). Tapping the notice opens the draft again
 * (Tyler, 2026-10-01).
 */
function useDraftKeptOnLeaving(invoice: Invoice | undefined) {
  // Deleting or filing a draft away navigates to the list, and saying the draft is waiting as it goes
  // is a plain lie about a thing that has just been thrown away (Tyler, 2026-09-22); those set the
  // returned ref.
  return useKeptOnLeaving(() => {
    if (invoice?.status !== "draft") return null;
    const name = [invoice.supplierName, invoice.invoiceNumber].filter(Boolean).join(" ");
    return {
      title: "Draft kept",
      body: `${name || "This invoice"} is waiting under Invoices, with its document.`,
      link: { kind: "invoice", invoiceId: invoice.id },
    };
  });
}
/**
 * What state the invoice is in, as an icon beside the name rather than the word "draft" sitting in
 * the title row (Tyler, 2026-09-21). The word is still there for a screen reader and on hover.
 */
function StatusIcon({ invoice }: { invoice: Invoice }) {
  const state = invoice.reversed
    ? { Icon: Undo2, tone: "put-back", label: "Put back" }
    : invoice.status === "draft"
      ? { Icon: FilePen, tone: "draft", label: "Draft, not yet received" }
      : { Icon: CircleCheck, tone: "received", label: "Received" };
  const { Icon } = state;
  return (
    <span className={`status-icon ${state.tone}`} title={state.label}>
      <Icon size={20} aria-hidden />
      <span className="sr-only">{state.label}</span>
    </span>
  );
}
/**
 * Binning a draft, and filing away one that has been put back.
 *
 * Undoing a receipt is on the notification that announced it, not here: it goes up into the bell and
 * stays pressable there (Tyler, 2026-09-23), where a button in this corner, appearing as the
 * notification came down over it, was two things competing for the same place. Once that notification
 * is gone, the History page still takes it back: the invoice's receipts are one entry, so undoing any
 * of them puts the whole invoice back.
 */
function InvoiceActions({
  invoice,
  onHandledDeparture,
}: {
  invoice: Invoice;
  onHandledDeparture: () => void;
}) {
  const navigate = useNavigate();
  const notify = useNotify();
  const [confirming, setConfirming] = useState(false);
  const remove = useChange(send.deleteInvoice, {
    onSuccess: () => {
      notify("Draft deleted");
      // Said its piece already; the screen going away is this, not the brewer wandering off.
      onHandledDeparture();
      navigate({ to: "/invoices" });
    },
  });
  const discard = useChange(send.discardInvoice, {
    onSuccess: () => {
      notify("Invoice filed away");
      onHandledDeparture();
      navigate({ to: "/invoices" });
    },
  });

  if (invoice.status === "draft") {
    return (
      <>
        <button
          type="button"
          className="button danger"
          disabled={remove.isPending}
          onClick={() => setConfirming(true)}
        >
          <Trash2 size={14} /> Delete draft
        </button>
        <Dialog open={confirming} onClose={() => setConfirming(false)} title="Delete this draft?">
          <DialogForm
            onSubmit={() => remove.mutate(invoice.id)}
            footer={
              <>
                <button type="button" className="button" onClick={() => setConfirming(false)}>
                  Keep it
                </button>
                <button type="submit" className="button danger" disabled={remove.isPending}>
                  Delete it
                </button>
              </>
            }
          >
            <p>
              Nothing on this invoice has reached stock, so there is no history to keep. The
              document goes with it.
            </p>
            <ErrorNote error={remove.error} />
          </DialogForm>
        </Dialog>
      </>
    );
  }
  // Put back already: nothing of it is in stock, so it can be filed away and its number freed.
  // Leaving it in the list with no way to move it was the complaint (Tyler, 2026-09-23).
  if (invoice.reversed) {
    return (
      <>
        <button
          type="button"
          className="button"
          disabled={discard.isPending}
          onClick={() => discard.mutate(invoice.id)}
          title="Nothing from this invoice is in stock. Filing it away frees its number."
        >
          <Archive size={14} /> {discard.isPending ? "Filing it away" : "File it away"}
        </button>
        <ErrorNote error={discard.error} />
      </>
    );
  }
  return null;
}
/**
 * Whether what is on the screen is safe, in the fewest words: saving as the brewer types, saved, or
 * not yet saved and trying again. Nothing at all until something has been changed.
 */
function SaveStatusNote({ status }: { status: SaveStatus }) {
  if (status === "idle") return null;
  return (
    <span className={`save-status ${status}`} role="status">
      {saveStatusText(status)}
    </span>
  );
}
